import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useImperativeHandle,
} from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  styled,
  TextField,
  Typography,
  Menu,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  MarkEmailRead,
  MarkEmailUnread,
  OpenInNew,
  Add,
  Close,
  Save,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import api from "../../services/api";
import AssigneeDropdown from "../../utils/AssigneeDropdown";
import { useWorkspace } from "../../utils/WorkspaceContext";
import useCurrentUser, { guestCapabilities } from "../../utils/useCurrentUser";
import useTaskListPatcher from "../../hooks/useTaskListPatcher";
import { getStatusColor, getStatusMeta, StatusBadge } from "../../utils/StatusChip";
import { DEFAULT_CATEGORY } from "../../utils/constants";
import { useTaskOverlay, taskHighlightSx } from "../../utils/TaskOverlayContext";
import BulkEditBar from "./BulkEditBar";
import TaskCreate, { duplicatePrefillFromTask } from "./TaskCreate";

/**
 * @typedef {Object} Task
 * @property {number} id - Task ID
 * @property {string} name - Task name
 * @property {string} status - Task status
 * @property {string} dueDate - Task due date
 * @property {number} lineUp - Task priority line up
 * @property {boolean} read - Whether task is read
 * @property {Object} assignee - Assigned user
 */

/**
 * @typedef {Object} Track
 * @property {number} id - Track ID
 * @property {string} name - Track name
 * @property {Task[]} tasks - Array of tasks in the track
 */

/**
 * @typedef {Object} Project
 * @property {number} id - Project ID
 * @property {string} name - Project name
 * @property {string} epic - Project epic name
 * @property {Track[]} tracks - Array of tracks in the project
 */

/**
 * @typedef {Object} ExpandedStates
 * @property {Object<number, boolean>} projects - Expanded state of projects
 * @property {Object<number, boolean>} tracks - Expanded state of tracks
 */

/**
 * @typedef {Object} CustomListViewProps
 * @property {Project[]} projects - Array of projects to display
 * @property {function} fetchProjects - Function to fetch projects
 * @property {function} setProjects - Function to update projects state
 * @property {boolean} defaultExpanded - Default expansion state for new items
 * @property {string} pageId - Unique identifier for the page
 */

// One date format used app-wide.
const DATE_FORMAT = "YYYY-MM-DD";

// The nested endpoints serialize tasks with camelCase display keys (see
// `dueDate`/`lineUp` in the backend), while task PATCHes use the model's
// snake_case field names. This maps the API field we send to the key the rows
// actually render, so optimistic updates are visible immediately instead of
// only after a page refresh.
const API_TO_DISPLAY_FIELD = {
  do_date: "dueDate",
  line_up: "lineUp",
};

// Fixed column widths (px) keep header + rows aligned and predictable across
// screen sizes. The task name flexes; the row scrolls horizontally if needed
// instead of wrapping/breaking on a 14" laptop.
const COLS = {
  checkbox: 40,
  colorBar: 4,
  actions: 72,
  assignee: 120,
  status: 128,
  do_date: 150,
  reviewer: 120,
  line_up: 60,
};
const ROW_MIN_WIDTH =
  COLS.checkbox + COLS.colorBar + COLS.actions + COLS.assignee + 200 + COLS.status +
  COLS.do_date + COLS.reviewer + COLS.line_up + 80;

// Bulk-edit field plumbing. Rows render camelCase display keys while the API
// PATCH uses snake_case model fields, so each bulk field needs: the optimistic
// display key to write locally, and a reader for the task's *current* value so
// we can restore it on undo. See API_TO_DISPLAY_FIELD for the same idea on
// inline edits.
const BULK_FIELD_DISPLAY = {
  assignee: "assignee",
  status: "status",
  line_up: "lineUp",
  // Bulk sprint edits now use the 4-slot abstraction: the backend resolves the
  // slot to each task's own track sprint, so a single value works across tracks.
  sprint_slot: "sprint_slot",
  division: "division_id",
  department: "department_id",
};
const READ_BULK_FIELD = {
  assignee: (t) => t.assignee ?? null,
  status: (t) => t.status ?? null,
  line_up: (t) => t.lineUp ?? null,
  sprint_slot: (t) => t.sprint_slot ?? null,
  division: (t) => t.division_id ?? null,
  department: (t) => t.department_id ?? null,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0.1),
  padding: theme.spacing(0.1),
}));

const ProjectHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0.5, 1),
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Per-track horizontal scroll container so dense rows never break the layout.
const ScrollArea = styled(Box)({
  width: "100%",
  overflowX: "auto",
});

const RowBase = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "nowrap",
  gap: theme.spacing(1),
  minWidth: ROW_MIN_WIDTH,
  marginLeft: theme.spacing(3),
  padding: theme.spacing(0.25, 1),
}));

const HeaderRow = styled(RowBase)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontWeight: "bold",
  fontSize: 12,
}));

const TaskRow = styled(RowBase)(({ theme }) => ({
  minHeight: 38,
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    cursor: "pointer",
  },
  "& input, & select, & button, & .MuiSelect-root, & .status-dropdown": {
    cursor: "text",
  },
  "& button, & .MuiSelect-root, & .status-dropdown": {
    cursor: "default",
  },
}));

// Minimal, low-weight status trigger that renders a StatusBadge via renderValue.
const StatusSelect = styled(Select)(() => ({
  "& .MuiSelect-select": {
    padding: "2px 24px 2px 4px !important",
    display: "flex",
    alignItems: "center",
  },
  "& fieldset": { border: "none" },
  "&:before, &:after": { display: "none" },
  "&:hover": { backgroundColor: "rgba(0,0,0,0.04)", borderRadius: 6 },
}));

const ColorBar = styled(Box)(({ color }) => ({
  width: "4px",
  alignSelf: "stretch",
  minHeight: 24,
  backgroundColor: color,
  borderRadius: "2px",
  flexShrink: 0,
}));

const IconGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.5),
  alignItems: "center",
  justifyContent: "flex-start",
}));

const NewTaskRow = styled(TaskRow)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  border: `2px dashed ${theme.palette.primary.main}`,
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
}));

/**
 * Fixed-width column wrapper used inside header/task rows.
 */
const Cell = ({ w, grow = false, children, sx }) => (
  <Box
    sx={{
      flex: grow ? "1 1 200px" : `0 0 ${w}px`,
      minWidth: grow ? 180 : undefined,
      width: grow ? undefined : w,
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * Custom nested task list component with project and track hierarchy
 * @component
 * @param {CustomListViewProps} props - Component props
 * @param {React.Ref} ref - Forwarded ref for external control
 * @returns {React.ReactElement} The nested task list component
 */
export const CustomListView = React.forwardRef(
  (
    {
      projects = [],
      fetchProjects,
      setProjects,
      defaultExpanded = false,
      pageId,
    },
    ref
  ) => {
    /**
     * Retrieves initial expanded states from session storage
     * @returns {ExpandedStates} Initial expanded states
     */
    const getInitialExpandedStates = () => {
      const stored = sessionStorage.getItem(`${pageId}-expanded-states`);
      if (stored) {
        return JSON.parse(stored);
      }
      return {
        projects: {},
        tracks: {},
      };
    };

    const [expandedStates, setExpandedStates] = useState(
      getInitialExpandedStates()
    );

    const { currentWorkspace } = useWorkspace();
    const { user: currentUser } = useCurrentUser();
    // Guest capability gating for the active workspace (no-op for non-guests).
    const guestCaps = guestCapabilities(currentUser, currentWorkspace?.id);
    const [contextMenu, setContextMenu] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    // Duplicate Task opens the create dialog seeded from the selected task.
    const [duplicatePrefill, setDuplicatePrefill] = useState(null);
    const { openTaskDetail, highlightTaskId } = useTaskOverlay();
    
    // New task management states
    const [newTasks, setNewTasks] = useState({});
    const [savingTasks, setSavingTasks] = useState({});
    const [snackbarState, setSnackbarState] = useState({
      open: false,
      message: "",
      severity: "error",
    });

    // Bulk-edit selection (List view only). Holds the set of selected task ids
    // plus the pending-undo snapshot shown in the undo toast.
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [undoState, setUndoState] = useState(null);

    useEffect(() => {
      if (pageId) {
        sessionStorage.setItem(
          `${pageId}-expanded-states`,
          JSON.stringify(expandedStates)
        );
      }
    }, [expandedStates, pageId]);

    // Load new tasks from localStorage on mount
    useEffect(() => {
      // Clear existing new tasks when pageId changes
      setNewTasks({});
      const storageKey = `${pageId}-new-tasks`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const parsedTasks = JSON.parse(stored);
          setNewTasks(parsedTasks);
        } catch (error) {
          console.error("Error loading new tasks from localStorage:", error);
        }
      }
    }, [pageId]);

    // Save new tasks to localStorage whenever they change
    useEffect(() => {
      if (pageId && Object.keys(newTasks).length > 0) {
        const storageKey = `${pageId}-new-tasks`;
        localStorage.setItem(storageKey, JSON.stringify(newTasks));
      } else if (pageId && Object.keys(newTasks).length === 0) {
        const storageKey = `${pageId}-new-tasks`;
        localStorage.removeItem(storageKey);
      }
    }, [newTasks, pageId]);

    const showSnackbar = (message, severity = "error") => {
      setSnackbarState({ open: true, message, severity });
    };

    const handleSnackbarClose = () => {
      setSnackbarState((prev) => ({ ...prev, open: false }));
    };

    /**
     * Debounced function to update task data
     * @param {number} taskId - Task ID to update
     * @param {Object} updates - Object containing fields to update
     */
    // Batched per task. A single shared timer used to cover every row, so
    // editing one task and then another inside the debounce window dropped the
    // first edit entirely. No refetch on success — the optimistic update
    // already happened.
    const debouncedUpdateTask = useTaskListPatcher({
      workspaceId: currentWorkspace?.id,
      onError: async () => {
        const updatedProjects = await fetchProjects();
        if (Array.isArray(updatedProjects)) {
          setProjects(updatedProjects);
        }
      },
    });

    // Give every project/track an entry so its chevron has a defined state.
    // Written as a functional update that bails when nothing is new: the
    // previous version also re-set state whenever `expandedStates.projects` was
    // empty, and since it depended on `expandedStates` and always built a fresh
    // object, an empty task list (no projects to add keys for) left that
    // condition permanently true and spun the component in a render loop.
    useEffect(() => {
      setExpandedStates((prev) => {
        const next = {
          projects: { ...prev.projects },
          tracks: { ...prev.tracks },
        };
        let hasNewItems = false;

        projects.forEach((project) => {
          if (!(project.id in next.projects)) {
            next.projects[project.id] = defaultExpanded;
            hasNewItems = true;
          }
          project.tracks?.forEach((track) => {
            if (!(track.id in next.tracks)) {
              next.tracks[track.id] = defaultExpanded;
              hasNewItems = true;
            }
          });
        });

        return hasNewItems ? next : prev;
      });
    }, [projects, defaultExpanded]);

    /**
     * Expands all projects and tracks
     */
    const expandAll = useCallback(() => {
      const newStates = {
        projects: {},
        tracks: {},
      };

      projects.forEach((project) => {
        newStates.projects[project.id] = true;
        project.tracks?.forEach((track) => {
          newStates.tracks[track.id] = true;
        });
      });

      setExpandedStates(newStates);
      if (pageId) {
        sessionStorage.setItem(
          `${pageId}-expanded-states`,
          JSON.stringify(newStates)
        );
      }
    }, [projects, pageId]);

    /**
     * Collapses all projects and tracks
     */
    const collapseAll = useCallback(() => {
      const newStates = {
        projects: {},
        tracks: {},
      };

      projects.forEach((project) => {
        newStates.projects[project.id] = false;
        project.tracks?.forEach((track) => {
          newStates.tracks[track.id] = false;
        });
      });

      setExpandedStates(newStates);
      if (pageId) {
        sessionStorage.setItem(
          `${pageId}-expanded-states`,
          JSON.stringify(newStates)
        );
      }
    }, [projects, pageId]);

    useImperativeHandle(
      ref,
      () => ({
        expandAll,
        collapseAll,
      }),
      [expandAll, collapseAll]
    );

    /**
     * Toggles project expansion state
     * @param {number} projectId - Project ID to toggle
     */
    const toggleProject = useCallback((projectId) => {
      setExpandedStates((prev) => ({
        ...prev,
        projects: {
          ...prev.projects,
          [projectId]: !prev.projects[projectId],
        },
      }));
    }, []);

    /**
     * Toggles track expansion state
     * @param {number} trackId - Track ID to toggle
     */
    const toggleTrack = useCallback((trackId) => {
      setExpandedStates((prev) => ({
        ...prev,
        tracks: {
          ...prev.tracks,
          [trackId]: !prev.tracks[trackId],
        },
      }));
    }, []);

    /**
     * Handles task field updates with optimistic UI updates
     * @param {number} taskId - Task ID to update
     * @param {string} field - Field name to update
     * @param {*} value - New field value
     */
    const handleTaskUpdate = useCallback((taskId, field, value) => {
      // Render rows read camelCase keys (e.g. dueDate/lineUp); patches use the
      // snake_case model field. Update the key the UI reads so the change shows
      // up immediately, then send the snake_case field to the API.
      const displayField = API_TO_DISPLAY_FIELD[field] || field;
      const updatedProjects = projects.map((project) => ({
        ...project,
        tracks: project.tracks.map((track) => ({
          ...track,
          tasks: track.tasks.map((task) =>
            task.id === taskId ? { ...task, [displayField]: value } : task
          ),
        })),
      }));
      setProjects(updatedProjects);

      // Don't push an incomplete numeric line_up (empty while typing) to the
      // API — keep the field responsive locally and patch once it's valid.
      if (field === "line_up" && (value === "" || value === null)) {
        return;
      }

      const updates = { [field]: value };
      debouncedUpdateTask(taskId, updates);
    }, [projects, setProjects, debouncedUpdateTask]);

    const statusOptions = useMemo(() => [
      "To Start",
      "Doing",
      "On hold",
      "Task Complete",
      "Documented",
      "Closed w Review",
    ], []);

    /**
     * Handles status change for a task
     * @param {React.ChangeEvent} e - Change event
     * @param {number} taskId - Task ID to update
     */
    const handleStatusChange = useCallback((e, taskId) => {
      e.stopPropagation();
      if (taskId) {
        handleTaskUpdate(taskId, "status", e.target.value);
      }
    }, [handleTaskUpdate]);

    /**
     * Toggles the read status of a task
     * @param {React.MouseEvent} e - Click event
     * @param {Task} task - Task to toggle
     */
    const handleReadToggle = useCallback((e, task) => {
      e.stopPropagation();
      if (task.id) {
        handleTaskUpdate(task.id, "read", !task.read);
      }
    }, [handleTaskUpdate]);

    /**
     * Handles row click to navigate to task details
     * @param {React.MouseEvent} e - Click event
     * @param {number} taskId - Task ID to navigate to
     */
    const handleRowClick = useCallback((e, taskId) => {
      openTaskDetail(taskId);
    }, [openTaskDetail]);

    /**
     * Handles assignee change for a task
     * @param {number} taskId - Task ID to update
     * @param {number} userId - New assignee user ID
     */
    const handleAssigneeChange = useCallback((taskId, userId) => {
      if (taskId) {
        handleTaskUpdate(taskId, "assignee", userId);
      }
    }, [handleTaskUpdate]);

    /**
     * Handles reviewer change for a task
     * @param {number} taskId - Task ID to update
     * @param {number} userId - New reviewer user ID
     */
    const handleReviewerChange = useCallback((taskId, userId) => {
      if (taskId) {
        handleTaskUpdate(taskId, "reviewer", userId);
      }
    }, [handleTaskUpdate]);

    // ---- Bulk edit (List view only) -------------------------------------
    // Flat id → task index for selection, single-track detection, and undo
    // snapshots, rebuilt only when the tree changes.
    const taskIndex = useMemo(() => {
      const map = new Map();
      (projects || []).forEach((p) =>
        (p.tracks || []).forEach((tr) =>
          (tr.tasks || []).forEach((t) => {
            if (t.id != null) map.set(t.id, { task: t, trackId: tr.id, trackName: tr.name });
          })
        )
      );
      return map;
    }, [projects]);

    const toggleTaskSelection = useCallback((taskId) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.has(taskId) ? next.delete(taskId) : next.add(taskId);
        return next;
      });
    }, []);

    const setTrackSelection = useCallback((track, checked) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        (track.tasks || []).forEach((t) => {
          if (t.id == null) return;
          checked ? next.add(t.id) : next.delete(t.id);
        });
        return next;
      });
    }, []);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    // Apply a per-id map of display-key patches onto the local tree.
    const patchTaskState = useCallback((perIdDisplay) => {
      setProjects((prev) =>
        (prev || []).map((p) => ({
          ...p,
          tracks: (p.tracks || []).map((tr) => ({
            ...tr,
            tasks: (tr.tasks || []).map((t) =>
              perIdDisplay[t.id] ? { ...t, ...perIdDisplay[t.id] } : t
            ),
          })),
        }))
      );
    }, [setProjects]);

    const toDisplayPatch = (apiUpdates) => {
      const display = {};
      Object.entries(apiUpdates).forEach(([f, v]) => {
        display[BULK_FIELD_DISPLAY[f] || f] = v;
      });
      return display;
    };

    /**
     * Bulk-applies `updates` (API field → value) to every selected task:
     * snapshots originals for undo, updates the tree optimistically, then
     * PATCHes each task. On any failure we surface it and refetch.
     */
    const handleBulkApply = useCallback(async (updates) => {
      const ids = [...selectedIds].filter((id) => taskIndex.has(id));
      if (ids.length === 0) return;

      const originals = {};
      ids.forEach((id) => {
        const { task } = taskIndex.get(id);
        const snap = {};
        Object.keys(updates).forEach((f) => {
          snap[f] = READ_BULK_FIELD[f] ? READ_BULK_FIELD[f](task) : task[f];
        });
        originals[id] = snap;
      });

      const display = toDisplayPatch(updates);
      const perId = {};
      ids.forEach((id) => { perId[id] = display; });
      patchTaskState(perId);
      clearSelection();

      const results = await Promise.allSettled(
        ids.map((id) =>
          api.patch(`tasks/${id}/`, { ...updates, workspace: currentWorkspace.id })
        )
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) {
        showSnackbar(`Failed to update ${failed} of ${ids.length} task(s)`, "error");
        const refreshed = await fetchProjects();
        if (Array.isArray(refreshed)) setProjects(refreshed);
        return;
      }
      setUndoState({ count: ids.length, originals });
    }, [selectedIds, taskIndex, patchTaskState, clearSelection, currentWorkspace, fetchProjects, setProjects]);

    const handleUndo = useCallback(async () => {
      const snapshot = undoState;
      if (!snapshot) return;
      setUndoState(null);

      const entries = Object.entries(snapshot.originals);
      const perId = {};
      entries.forEach(([id, apiVals]) => { perId[id] = toDisplayPatch(apiVals); });
      patchTaskState(perId);

      const results = await Promise.allSettled(
        entries.map(([id, apiVals]) =>
          api.patch(`tasks/${id}/`, { ...apiVals, workspace: currentWorkspace.id })
        )
      );
      if (results.some((r) => r.status === "rejected")) {
        const refreshed = await fetchProjects();
        if (Array.isArray(refreshed)) setProjects(refreshed);
      }
    }, [undoState, patchTaskState, currentWorkspace, fetchProjects, setProjects]);

    if (!Array.isArray(projects) || projects.length === 0) {
      return <Alert severity="info">No tasks available</Alert>;
    }

    /**
     * Handles right-click context menu for tasks
     * @param {React.MouseEvent} event - Context menu event
     * @param {Task} task - Task to show context menu for
     */
    const handleTaskContextMenu = (event, task) => {
      event.preventDefault();
      setContextMenu({
        mouseX: event.clientX + 2,
        mouseY: event.clientY - 6,
      });
      setSelectedTask(task);
    };

    /**
     * Closes the context menu
     */
    const handleCloseContextMenu = () => {
      setContextMenu(null);
      setSelectedTask(null);
    };

    /**
     * Opens the create dialog carrying every field of the selected task except
     * its name, notes and action items, which the user fills in fresh.
     */
    const handleDuplicateTask = () => {
      if (!selectedTask) return;
      setDuplicatePrefill(duplicatePrefillFromTask(selectedTask));
      handleCloseContextMenu();
    };

    /**
     * Deletes a task
     * @async
     */
    const handleDeleteTask = async () => {
      if (!selectedTask?.id) return;
      try {
        const response = await api.delete(`tasks/${selectedTask.id}/`, {
          params: { workspace_id: currentWorkspace.id },
        });
        if (response.status === 204) {
          const updatedProjects = projects.map((project) => ({
            ...project,
            tracks: project.tracks.map((track) => ({
              ...track,
              tasks: track.tasks.filter((task) => task.id !== selectedTask.id),
            })),
          }));
          setProjects(updatedProjects);
        } else {
          alert("Task delete failed");
        }
      } catch (error) {
        console.error("Error deleting task:", error);
        alert("Task delete failed");
      }
      handleCloseContextMenu();
    };

    /**
     * Adds a new task form for a specific track
     * @param {number} trackId - Track ID to add task to
     * @param {number} projectId - Project ID the track belongs to
     */
    const handleAddTask = async (trackId, projectId) => {
      const tempId = `temp-${trackId}-${Date.now()}`;

      const newTask = {
        tempId,
        trackId,
        projectId,
        name: "",
        category: DEFAULT_CATEGORY,
        task_class: "Core",
        status: "To Start",
        do_date: null,
        assignee: null,
        reviewer: null,
        line_up: 30,
        workspace: currentWorkspace.id,
        // 4-slot abstraction: new tasks default to 'current'. The backend maps
        // it to the track's current sprint at save time.
        sprint_slot: "current",
      };
      setNewTasks((prev) => ({
        ...prev,
        [tempId]: newTask,
      }));
    };

    /**
     * Updates a field in the new task being created
     * @param {string} tempId - Temporary ID of the task
     * @param {string} field - Field name to update
     * @param {*} value - New field value
     */
    const handleNewTaskUpdate = (tempId, field, value) => {
      setNewTasks((prev) => ({
        ...prev,
        [tempId]: {
          ...prev[tempId],
          [field]: value,
        },
      }));
    };

    /**
     * Removes an unsaved task
     * @param {string} tempId - Temporary ID of the task to remove
     */
    const handleRemoveNewTask = (tempId) => {
      setNewTasks((prev) => {
        const updated = { ...prev };
        delete updated[tempId];
        return updated;
      });
    };

    /**
     * Validates if a new task has all required fields filled
     * @param {Object} task - Task object to validate
     * @returns {Object} Validation result with isValid flag and error message
     */
    const validateTask = (task) => {
      // Check in order: name, assignee. Sprint is slot-based and always defaults
      // to 'current', so it no longer needs validation.
      if (!task.name || task.name.trim() === "") {
        return { isValid: false, message: "Task name is required" };
      }
      if (!task.assignee) {
        return { isValid: false, message: "Assignee is required" };
      }
      return { isValid: true, message: "" };
    };

    /**
     * Saves a new task to the backend
     * @param {string} tempId - Temporary ID of the task
     * @async
     */
    const handleSaveNewTask = async (tempId) => {
      const task = newTasks[tempId];
      
      const validation = validateTask(task);
      if (!validation.isValid) {
        showSnackbar(validation.message, "error");
        return;
      }

      setSavingTasks((prev) => ({ ...prev, [tempId]: true }));

      try {
        const taskData = {
          name: task.name,
          category: task.category || DEFAULT_CATEGORY,
          task_class: task.task_class || "Core",
          status: task.status,
          line_up: task.line_up,
          workspace: task.workspace,
          track: task.trackId,
          assignee: task.assignee,
          reviewer: task.reviewer,
          do_date: task.do_date,
          sprint_slot: task.sprint_slot || "current",
        };

        const response = await api.post("tasks/", taskData);
        
        if (response.status === 201) {
          // Remove from new tasks
          handleRemoveNewTask(tempId);
          
          // Refresh the projects list. Guarded like the other call sites — a
          // fetch that returns nothing would otherwise blank the tree and make
          // the next render throw on `projects.map`.
          const updatedProjects = await fetchProjects();
          if (Array.isArray(updatedProjects)) setProjects(updatedProjects);

          showSnackbar("Task created successfully", "success");
        }
      } catch (error) {
        console.error("Error saving new task:", error);
        showSnackbar(
          "Failed to save task: " + (error.response?.data?.detail || error.message),
          "error"
        );
      } finally {
        setSavingTasks((prev) => {
          const updated = { ...prev };
          delete updated[tempId];
          return updated;
        });
      }
    };

    return (
      <StyledPaper elevation={1}>
        {projects &&
          projects?.map((project) => (
            <Box key={project?.id}>
              <ProjectHeader onClick={() => toggleProject(project.id)}>
                {expandedStates.projects[project?.id] ? (
                  <KeyboardArrowDown />
                ) : (
                  <KeyboardArrowRight />
                )}
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {project?.epic} - {project?.name}
                </Typography>
              </ProjectHeader>

              {expandedStates.projects[project?.id] &&
                project?.tracks?.map((track) => (
                  <Box key={track.id} sx={{ ml: 3 }}>
                    <ProjectHeader onClick={() => toggleTrack(track?.id)}>
                      {expandedStates?.tracks[track.id] ? (
                        <KeyboardArrowDown />
                      ) : (
                        <KeyboardArrowRight />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{track?.name}</Typography>
                    </ProjectHeader>

                    {expandedStates.tracks[track.id] && (
                      <ScrollArea>
                        <HeaderRow>
                          <Cell w={COLS.checkbox} sx={{ display: "flex", alignItems: "center" }}>
                            {(() => {
                              const ids = (track.tasks || [])
                                .map((t) => t.id)
                                .filter((id) => id != null);
                              const selCount = ids.filter((id) => selectedIds.has(id)).length;
                              return (
                                <Checkbox
                                  size="small"
                                  sx={{ p: 0 }}
                                  checked={ids.length > 0 && selCount === ids.length}
                                  indeterminate={selCount > 0 && selCount < ids.length}
                                  onChange={(e) => setTrackSelection(track, e.target.checked)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              );
                            })()}
                          </Cell>
                          <ColorBar color="transparent" />
                          <Cell w={COLS.actions}><Typography variant="inherit" noWrap>Actions</Typography></Cell>
                          <Cell w={COLS.assignee}><Typography variant="inherit" noWrap>Assignee</Typography></Cell>
                          <Cell grow><Typography variant="inherit" noWrap>Task Name</Typography></Cell>
                          <Cell w={COLS.status}><Typography variant="inherit" noWrap>Status</Typography></Cell>
                          <Cell w={COLS.do_date}><Typography variant="inherit" noWrap>Do Date</Typography></Cell>
                          <Cell w={COLS.reviewer}><Typography variant="inherit" noWrap>Reviewer</Typography></Cell>
                          <Cell w={COLS.line_up}><Typography variant="inherit" noWrap>Line up</Typography></Cell>
                        </HeaderRow>

                        {track?.tasks?.map((task, index) => (
                          <TaskRow
                            key={task.id || task.tempId || index}
                            onContextMenu={(e) => handleTaskContextMenu(e, task)}
                            sx={{
                              ...(task.id === highlightTaskId ? taskHighlightSx : {}),
                              ...(task.id && selectedIds.has(task.id)
                                ? { backgroundColor: "action.selected" }
                                : {}),
                            }}
                          >
                            <Cell w={COLS.checkbox} sx={{ display: "flex", alignItems: "center" }}>
                              <Checkbox
                                size="small"
                                sx={{ p: 0 }}
                                checked={task.id != null && selectedIds.has(task.id)}
                                disabled={task.id == null}
                                onChange={() => task.id != null && toggleTaskSelection(task.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Cell>
                            <ColorBar color={getStatusColor(task.status).bg} />
                            <Cell w={COLS.actions}>
                              <IconGroup>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleReadToggle(e, task)}
                                  color={task.read ? "default" : "secondary"}
                                >
                                  {task.read ? (
                                    <MarkEmailRead fontSize="small" />
                                  ) : (
                                    <MarkEmailUnread fontSize="small" />
                                  )}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleRowClick(e, task.id)}
                                  color="default"
                                >
                                  <OpenInNew fontSize="small" />
                                </IconButton>
                              </IconGroup>
                            </Cell>
                            <Cell w={COLS.assignee}>
                              <AssigneeDropdown
                                currentSelected={task.assignee}
                                onChange={(userId) =>
                                  handleAssigneeChange(task.id, userId)
                                }
                              />
                            </Cell>
                            <Cell grow>
                              <TextField
                                fullWidth
                                variant="standard"
                                value={task.name}
                                onChange={(e) =>
                                  task.id &&
                                  handleTaskUpdate(task.id, "name", e.target.value)
                                }
                                inputProps={{ style: { fontSize: 13 } }}
                                sx={{ "& .MuiInputBase-input": { textOverflow: "ellipsis" } }}
                              />
                            </Cell>
                            <Cell w={COLS.status}>
                              <StatusSelect
                                value={task.status || ""}
                                onChange={(e) => handleStatusChange(e, task.id)}
                                onClick={(e) => e.stopPropagation()}
                                displayEmpty
                                renderValue={(val) => <StatusBadge status={val || "default"} />}
                              >
                                {statusOptions.map((status) => {
                                  const meta = getStatusMeta(status);
                                  return (
                                    <MenuItem key={status} value={status} sx={{ gap: 1, fontSize: 13 }}>
                                      <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: meta.solid, flexShrink: 0 }} />
                                      {meta.short}
                                    </MenuItem>
                                  );
                                })}
                              </StatusSelect>
                            </Cell>
                            <Cell w={COLS.do_date}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  format={DATE_FORMAT}
                                  value={task.dueDate ? dayjs(task.dueDate) : null}
                                  onChange={(newDate) =>
                                    task.id &&
                                    handleTaskUpdate(
                                      task.id,
                                      "do_date",
                                      newDate ? dayjs(newDate).format("YYYY-MM-DD") : null
                                    )
                                  }
                                  slots={{
                                    textField: (params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        fullWidth
                                        variant="standard"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    ),
                                  }}
                                />
                              </LocalizationProvider>
                            </Cell>
                            <Cell w={COLS.reviewer}>
                              <AssigneeDropdown
                                currentSelected={task.reviewer}
                                onChange={(userId) =>
                                  handleReviewerChange(task.id, userId)
                                }
                              />
                            </Cell>
                            <Cell w={COLS.line_up}>
                              <TextField
                                variant="standard"
                                type="number"
                                value={task.lineUp ?? ""}
                                fullWidth
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  if (!task.id) return;
                                  const raw = e.target.value;
                                  handleTaskUpdate(
                                    task.id,
                                    "line_up",
                                    raw === "" ? "" : Number(raw)
                                  );
                                }}
                                inputProps={{ style: { fontSize: 13 } }}
                              />
                            </Cell>
                          </TaskRow>
                        ))}

                        {/* Render new tasks for this track */}
                        {Object.entries(newTasks)
                          .filter(([_, task]) => task.trackId === track.id)
                          .map(([tempId, task]) => (
                            <NewTaskRow key={tempId}>
                              <Cell w={COLS.checkbox} />
                              <ColorBar color="#3B82F6" />
                              <Cell w={COLS.actions}>
                                <IconGroup>
                                  <Tooltip title="Remove task">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveNewTask(tempId)}
                                      color="error"
                                      disabled={savingTasks[tempId]}
                                    >
                                      <Close fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Save task">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleSaveNewTask(tempId)}
                                      color="primary"
                                      disabled={
                                        savingTasks[tempId] || !validateTask(task).isValid
                                      }
                                    >
                                      {savingTasks[tempId] ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        <Save fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </IconGroup>
                              </Cell>
                              <Cell w={COLS.assignee}>
                                <AssigneeDropdown
                                  currentSelected={task.assignee}
                                  onChange={(userId) =>
                                    handleNewTaskUpdate(tempId, "assignee", userId)
                                  }
                                />
                              </Cell>
                              <Cell grow>
                                <TextField
                                  fullWidth
                                  variant="standard"
                                  placeholder="Task name *"
                                  value={task.name}
                                  onChange={(e) =>
                                    handleNewTaskUpdate(tempId, "name", e.target.value)
                                  }
                                  error={!task.name || task.name.trim() === ""}
                                  autoFocus
                                  inputProps={{ style: { fontSize: 13 } }}
                                />
                              </Cell>
                              <Cell w={COLS.status}>
                                <StatusSelect
                                  value={task.status || ""}
                                  onChange={(e) =>
                                    handleNewTaskUpdate(tempId, "status", e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  displayEmpty
                                  renderValue={(val) => <StatusBadge status={val || "default"} />}
                                >
                                  {statusOptions.map((status) => {
                                    const meta = getStatusMeta(status);
                                    return (
                                      <MenuItem key={status} value={status} sx={{ gap: 1, fontSize: 13 }}>
                                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: meta.solid, flexShrink: 0 }} />
                                        {meta.short}
                                      </MenuItem>
                                    );
                                  })}
                                </StatusSelect>
                              </Cell>
                              <Cell w={COLS.do_date}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <DatePicker
                                    format={DATE_FORMAT}
                                    value={task.do_date ? dayjs(task.do_date) : null}
                                    onChange={(newDate) =>
                                      handleNewTaskUpdate(
                                        tempId,
                                        "do_date",
                                        newDate ? dayjs(newDate).format("YYYY-MM-DD") : null
                                      )
                                    }
                                    slots={{
                                      textField: (params) => (
                                        <TextField
                                          {...params}
                                          size="small"
                                          fullWidth
                                          variant="standard"
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      ),
                                    }}
                                  />
                                </LocalizationProvider>
                              </Cell>
                              <Cell w={COLS.reviewer}>
                                <AssigneeDropdown
                                  currentSelected={task.reviewer}
                                  onChange={(userId) =>
                                    handleNewTaskUpdate(tempId, "reviewer", userId)
                                  }
                                />
                              </Cell>
                              <Cell w={COLS.line_up}>
                                <TextField
                                  variant="standard"
                                  type="number"
                                  value={task.line_up}
                                  fullWidth
                                  onChange={(e) =>
                                    handleNewTaskUpdate(
                                      tempId,
                                      "line_up",
                                      isNaN(parseInt(e.target.value)) ? 30 : parseInt(e.target.value)
                                    )
                                  }
                                  inputProps={{ style: { fontSize: 13 } }}
                                />
                              </Cell>
                            </NewTaskRow>
                          ))}

                        {/* Add Task Button (hidden for guests without the flag) */}
                        {guestCaps.canManageTasks && (
                          <Box sx={{ display: "flex", alignItems: "center", ml: 3, py: 0.5 }}>
                            <Box sx={{ width: COLS.colorBar, mr: 1 }} />
                            <Tooltip title="Add new task">
                              <IconButton
                                size="small"
                                onClick={() => handleAddTask(track.id, project.id)}
                                color="primary"
                                sx={{
                                  backgroundColor: "primary.main",
                                  color: "white",
                                  "&:hover": { backgroundColor: "primary.dark" },
                                }}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </ScrollArea>
                    )}
                  </Box>
                ))}
            </Box>
          ))}
        <Menu
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
        >
          {guestCaps.canManageTasks && (
            <MenuItem onClick={handleDuplicateTask}>Duplicate Task</MenuItem>
          )}
          {guestCaps.canManageTasks && (
            <MenuItem
              onClick={handleDeleteTask}
              sx={{ color: "error.main", "&:hover": { bgcolor: "error.light" } }}
            >
              Delete Task
            </MenuItem>
          )}
        </Menu>
        <TaskCreate
          open={Boolean(duplicatePrefill)}
          prefill={duplicatePrefill}
          onClose={() => setDuplicatePrefill(null)}
          onTaskCreated={async () => {
            const refreshed = await fetchProjects();
            if (Array.isArray(refreshed)) setProjects(refreshed);
          }}
        />
        <Snackbar
          open={snackbarState.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarState.severity}
            sx={{ width: "100%" }}
          >
            {snackbarState.message}
          </Alert>
        </Snackbar>

        {selectedIds.size > 0 && (
          <BulkEditBar
            selectedCount={selectedIds.size}
            statusOptions={statusOptions}
            onApply={handleBulkApply}
            onClear={clearSelection}
          />
        )}

        {/* 10-second undo window for the last bulk change. */}
        <Snackbar
          open={Boolean(undoState)}
          autoHideDuration={10000}
          onClose={() => setUndoState(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          message={undoState ? `Updated ${undoState.count} task(s)` : ""}
          action={
            <Button color="secondary" size="small" onClick={handleUndo}>
              UNDO
            </Button>
          }
        />
      </StyledPaper>
    );
  }
);

export default CustomListView;
